import { PropertyService } from './properties/property.service';
import {
  throwError as observableThrowError,
  of as observableOf,
  Observable,
  of,
  EMPTY
} from 'rxjs';

import { catchError, map, finalize, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Configuration } from './conf/app.constants';
import { LoginResponse, PasswordStrengthDTO } from './api/plyca-dtos';
import { Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from './store/AppState';
import { DELETE_PENDINGTASKS } from './store/pendingTasks';
import { DELETE_USER, SET_USER } from './store/user';
import { SessionInfo } from './api/UserPrefs-dto';
import { TaskService } from './tasks/task.service';
import { AppConfiguration } from './conf/app.configuration';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpParams
} from '../../node_modules/@angular/common/http';
import { OpenedTaskInfo } from './api/OpenedTaskInfo';
import { TranslateService } from '../../node_modules/@ngx-translate/core';
import { SignatureService } from '../services/signature.service';
import { InitService } from '../services/init.service';
import { DELETE_OPENEDTASKS } from './store/openedTasks';
import { BaseDirectiveObject } from './components/common/base-directive-object';
import { PlycaErrorHandler } from '../http/PlycaErrorHandler';
import { SET_SESSIONREFRESH } from './store/sessionRefreshState';
import { CLEAR_ENSALERTVIEWSTATE } from './store/userPrefs';
import { ConfirmationService } from 'primeng-lts/api';

export class AuthHttpError extends Error {
  constructor(m: string) {
    super(m);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, AuthHttpError.prototype);
  }
}

/*
 *  Ver https://github.com/auth0/angular2-jwt y http://blog.ionic.io/ionic-2-and-auth0/ para refresco de token
 */
@Injectable()
export class AuthService extends BaseDirectiveObject {
  private jwtHelper: JwtHelperService = new JwtHelperService();
  private sessionInfo: any;
  private openedTasks: OpenedTaskInfo[];


  constructor(
    private http: HttpClient,
    private appConfig: AppConfiguration,
    private configuration: Configuration,
    private taskService: TaskService,
    private router: Router,
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService,
    private signatureService: SignatureService,
    private initService: InitService,
    private propertyService: PropertyService,
    private errorHandler: PlycaErrorHandler
  ) {
    super();
    this.subscribe(store.select('user'),
      sesinfo => {
        this.sessionInfo = sesinfo;
      });
    this.subscribe(store.select('openedTasks'),
      (openedTasks: OpenedTaskInfo[]) => {
        this.openedTasks = openedTasks;
      });
  }

  private getLoginRoute(): string {
    return this.appConfig.getValue('login.page');
  }

  public isPasswordModifiable(): Observable<boolean> {
    // Falta BD

    // BD login, ldap login
    // siglesignon / azure / cas sso
    // login, login - cert
    let loginPage = this.getLoginRoute();
    return this.propertyService.getProperty('TIPO_VALIDACION_USUARIOS', '').pipe(
      map(
        (value: string) => (value == 'BD') && (!loginPage || loginPage === 'login')

      ));

  }

  public basicLogin(user, password, code): Observable<HttpResponse<LoginResponse>> {
    let credsObject: any = code == null ? {} : { 'code': code };
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Basic ' + btoa(unescape(encodeURIComponent(user + ':' + password)))
      }),
      params: null
    };
    if (code) {
      let params = new HttpParams();
      params = params.append('code', code);
      httpOptions.params = params;
    }
    return this.internalLogin(credsObject, httpOptions);
  }

  /**
   * Permite el acceso al login por https si el servidor esta configurado para ello
   */
  public certLogin(): Observable<HttpResponse<LoginResponse>> {
    let credsObject: any = {};
    return this.internalLogin(credsObject, undefined);
  }

  public processSsoLogin(loginResponse: string | LoginResponse): void {
    console.log('sso-login');
    if (loginResponse) {
      if (typeof loginResponse === 'string') {
        this.processLoginResponse(JSON.parse(loginResponse));
      } else {
        this.processLoginResponse(loginResponse);
      }
    } else {
      console.error.apply('No hay informacion de login almacenada');
    }
  }

  private internalLogin(
    credsObject: any,
    options: any
  ): Observable<HttpResponse<LoginResponse>> {
    console.log('login');
    options = options || {};
    options.observe = 'response';
    let actionUrl = this.configuration.ServerWithPublicApiUrl + 'auth/login';
    return this.http
      .post<LoginResponse>(actionUrl, JSON.stringify(credsObject), options)
      .pipe(
        tap((value: HttpResponse<LoginResponse>) =>
          this.processLoginResponse(value.body)
        ),
        catchError((err: any) => {
          // por si acaso limpiamos sesion
          this.exit();
          return observableThrowError(err);
        })
      );
  }

  private processLoginResponse(
    value: LoginResponse
  ): Observable<LoginResponse> {
    this.initSessionInfo(value);
    // navegar a la url
    let url = '/';
    let redirectTo: string = sessionStorage.getItem('login.redirectTo');
    if (redirectTo) {
      url = redirectTo;
    } else {
      url = 'mainView';
    }
    this.router.navigate([url]);
    // limpiamos url
    sessionStorage.removeItem('login.redirectTo');
    return observableOf(value);
  }

  private initSessionInfo(loginResponse: LoginResponse): void {
    console.log('login ejecutado ', loginResponse);
    this.createSessionInfo(loginResponse);
    if (loginResponse.user && !loginResponse.user.requires2FA) {
      this.initService.init();
    }
  }

  logout(confirm = true) {
    if (this.isLoggedIn()) {
      if (confirm) {
        // consultar cierre
        this.confirmationService.confirm({
          message: this.translateService.instant('Navigation.exit.confirmation'),
          accept: () => {
            this.acceptLogout();
          }
        });
      } else {
        this.acceptLogout();
      }
    } else {
      this.internalLogout();
    }
  }

  private acceptLogout() {
    if (this.openedTasks && this.openedTasks.length > 0) {
      // se solicita el cierre -> desbloquear tareas
      this.openedTasks.forEach(
        (
          taskInfo: OpenedTaskInfo,
          index: number,
          openedTasks: OpenedTaskInfo[]
        ) => this.subscribe(this.taskService
          .unlockTask(taskInfo.taskDTO)
          .pipe(
            finalize(() => {
              if (index === openedTasks.length - 1) {
                this.internalLogout();
              }
            })
          ))
      );
    } else {
      this.internalLogout();
    }
  }

  private internalLogout() {
    console.log('logout');
    let actionUrl = this.configuration.ServerWithAuthApiUrl + 'auth/logout';
    try {
      this.subscribe(this.http
        .get(actionUrl, { observe: 'response' })
        .pipe(
          catchError((error: any) => {
            if (error.status === 403 || error.status === 401) {
              // devuelvo un dato para continuar con el tap
              return of({});
            } else {
              this.errorHandler.showServerError(error);
              // devuelvo EMPTY para saltar los operadores de pipe e ir a finally/finalize si lo hay
              return EMPTY;
            }
          }),
          tap(() => {
            this.exit();
          })
        ));
    } catch (e) {
      console.error(e);
    }
  }

  public cleanSessionInfo(cleanUser = true): void {
    // limpiar los datos de la sesion
    if (cleanUser) {
      this.store.dispatch(DELETE_USER({}));
    }
    this.store.dispatch(DELETE_PENDINGTASKS());
    this.store.dispatch(DELETE_OPENEDTASKS({}));
    this.store.dispatch(SET_SESSIONREFRESH({ state: false }));



    // parar el hilo de consulta de tareas
    // this.taskService.stopTasksThread();
    this.initService.finish();
  }

  isLoggedIn(validateTokens = false): boolean {
    return (
      this.sessionInfo &&
      this.sessionInfo.accessToken &&
      this.sessionInfo.refreshToken &&
      this.sessionInfo.user &&
      (!validateTokens ||
        !this.jwtHelper.isTokenExpired(this.sessionInfo.accessToken) ||
        !this.jwtHelper.isTokenExpired(this.sessionInfo.refreshToken))
    );
  }

  public requires2FA(): boolean {
    return this.sessionInfo?.user?.requires2FA && !this.sessionInfo?.user?.authenticated2FA;
  }

  public requires2FAConfig(): boolean {
    return this.sessionInfo?.user?.needsNew2FACode;
  }

  public isTokenValid(): boolean {
    return this.sessionInfo && this.sessionInfo.accessToken && !this.jwtHelper.isTokenExpired(this.sessionInfo.accessToken);
  }

  private createSessionInfo(response: LoginResponse): void {
    console.log('processTokenResponse');
    if (
      !response.accessToken ||
      !response.refreshToken ||
      this.jwtHelper.isTokenExpired(response.accessToken)
    ) {
      throw new AuthHttpError('Invalid access token');
    }
    let sessionInfo: SessionInfo = new SessionInfo();
    sessionInfo.user = response.user;
    sessionInfo.accessToken = response.accessToken;
    sessionInfo.refreshToken = response.refreshToken;
    sessionInfo.version = response.version;
    this.store.dispatch(SET_USER({ sessionInfo: sessionInfo }));
    this.cleanSessionInfo(false);
  }

  getVersion(): string {
    if (this.sessionInfo) {
      return this.sessionInfo.version;
    } else {
      return ' - ';
    }
  }

  public requestResetPassword(userOrEmail: any): Observable<any> {
    let body = {
      'userId': userOrEmail
    };
    const httpOptions = {
      params: null
    }; let actionUrl = this.configuration.ServerWithPublicApiUrl + 'users/passwords/requestnewpassword';
    return this.http.post(actionUrl, JSON.stringify(body), httpOptions);
  }



  public resetPassword(userId, newPassword, token): Observable<PasswordStrengthDTO> {

    let body: any = {
      'userId': userId,
      'newPassword': newPassword,
      'token': token
    };

    const httpOptions = {
      params: null
    };

    let actionUrl = this.configuration.ServerWithPublicApiUrl + 'users/passwords/resetpassword';
    return this.http.post<PasswordStrengthDTO>(actionUrl, JSON.stringify(body), httpOptions);
  }




  public updatePassword(oldPassword, newPassword): Observable<any> {

    let body: any = {
      'oldPassword': oldPassword,
      'newPassword': newPassword
    };

    const httpOptions = {
      params: null
    };

    let actionUrl = this.configuration.URL_BASE + this.configuration.USERS_SERVICE_URL + 'passwords';
    return this.http.put(actionUrl, JSON.stringify(body), httpOptions);
  }


  public passwordStrength(userId, newPassword, token): Observable<PasswordStrengthDTO> {

    let body: any = {
      'userId': userId,
      'newPassword': newPassword,
      'token': token
    };

    const httpOptions = {
      params: null
    };

    let actionUrl = this.configuration.ServerWithPublicApiUrl + 'users/passwords/passwordstrength';
    return this.http.post<PasswordStrengthDTO>(actionUrl, JSON.stringify(body), httpOptions);
  }

  public exit(): void {
    this.cleanSessionInfo();
    this.navigateToLogoutRoute();
  }

  public navigateToLogoutRoute(): void {
    let navigateTo = this.getLoginRoute();
    if (navigateTo === 'sso-login' || navigateTo === 'sso-selection') {
      // en el caso de sistemas de tipo sso mostramos página de logout
      // que muestra aviso y así no entra de nuevo con las mismas credenciales
      navigateTo = 'sso-logout';
    }
    this.router.navigate([navigateTo]);
  }

  public navigateToLoginRoute(queryParams?: Params): void {
    this.router.navigate([this.getLoginRoute()], { queryParams: queryParams });
  }

}
